import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, UploadSimple, Phone, Copy, Package, Palette, FileArrowDown } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const imgSrc = (v) => (!v ? "" : /^https?:\/\//.test(v) ? v : `${API}/site/media/${v}`);
const clone = (o) => JSON.parse(JSON.stringify(o));

function Field({ label, value, onChange, textarea, rows = 3, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {textarea ? <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/site/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path); toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-3">
        {value ? <img src={imgSrc(value)} alt="" className="h-16 w-16 rounded-md object-cover border border-border" /> : <div className="h-16 w-16 rounded-md border border-dashed border-border bg-muted/40" />}
        <div className="flex-1 space-y-1.5">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="gap-1.5"><UploadSimple size={14} /> {busy ? "Uploading…" : "Upload"}</Button>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
    </div>
  );
}

function StringList({ label, items, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="h-9 w-9 hover:text-destructive"><Trash size={14} /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, ""])} className="gap-1.5"><Plus size={14} /> Add</Button>
    </div>
  );
}

function Card({ children }) { return <div className="bg-card border border-border rounded-md p-5 space-y-4">{children}</div>; }

function ColorField({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-3">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded border border-border bg-transparent p-1 cursor-pointer" />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#D81B4A" className="w-44 font-mono" />
        <span className="h-9 flex-1 rounded-md border border-border" style={{ background: value || "transparent" }} />
      </div>
    </div>
  );
}

function FileField({ label, value, filename, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/site/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path, data.filename); toast.success("File uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground"><FileArrowDown size={18} /></span>
        <div className="flex-1 space-y-1.5">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="File path or upload" />
          {filename && <p className="text-xs text-muted-foreground truncate">{filename}</p>}
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="gap-1.5"><UploadSimple size={14} /> {busy ? "Uploading…" : "Upload file"}</Button>
          <input ref={ref} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
    </div>
  );
}

function QuotesInbox() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/site/quotes").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const handle = async (id) => { await api.patch(`/site/quotes/${id}`); load(); };
  if (rows.length === 0) return <div className="h-32 flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border rounded-md">No quote requests yet.</div>;
  return (
    <div className="space-y-3" data-testid="quotes-inbox">
      {rows.map((q) => (
        <div key={q.id} data-testid={`quote-row-${q.id}`} className={`bg-card border border-border rounded-md p-4 ${q.handled ? "opacity-50" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{q.name} <span className="text-muted-foreground font-normal">· {q.service || "General"}</span></p>
              <p className="text-xs text-muted-foreground mt-0.5">
                <a href={`tel:${q.phone}`} className="text-primary hover:underline">{q.phone}</a>
                {q.email ? ` · ${q.email}` : ""}{q.location ? ` · ${q.location}` : ""}
              </p>
              {q.message && <p className="mt-2 text-sm">{q.message}</p>}
              <p className="mt-1 text-[11px] text-muted-foreground">{new Date(q.created_at).toLocaleString()}</p>
            </div>
            {q.handled ? <span className="text-xs text-success shrink-0">✓ Handled</span>
              : <Button size="sm" variant="outline" onClick={() => handle(q.id)} data-testid={`quote-handle-${q.id}`} className="shrink-0">Mark handled</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

const emptyOrder = { patient_name: "", phone: "", device: "", category: "", status: "", eta: "", note: "" };

function OrdersManager({ stages }) {
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ ...emptyOrder });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/site/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.patient_name.trim()) { toast.error("Patient name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/site/orders", { ...form, status: form.status || stages[0] || "Order Received" });
      toast.success(`Order created: ${data.order_code}`);
      setForm({ ...emptyOrder }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const updateStatus = async (o, status, note = "") => {
    try { await api.patch(`/site/orders/${o.id}`, { status, note }); load(); toast.success("Order updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const del = async (o) => { if (!window.confirm(`Delete order ${o.order_code}?`)) return; await api.delete(`/site/orders/${o.id}`); load(); };
  const copy = (code) => { navigator.clipboard?.writeText(`${window.location.origin}/track-order?id=${encodeURIComponent(code)}`); toast.success("Tracking link copied"); };

  const inputCls = "w-full rounded-md border border-border bg-background p-2.5 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-6" data-testid="orders-manager">
      <Card>
        <p className="text-sm font-semibold flex items-center gap-2"><Package size={16} /> New order</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Patient name *" value={form.patient_name} onChange={(v) => set("patient_name", v)} />
          <Field label="Phone (for verified tracking)" value={form.phone} onChange={(v) => set("phone", v)} />
          <Field label="Device / item" value={form.device} onChange={(v) => set("device", v)} />
          <Field label="Category" value={form.category} onChange={(v) => set("category", v)} />
          <div className="space-y-1.5">
            <Label className="text-xs">Initial status</Label>
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
              {stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Estimated ready date" value={form.eta} onChange={(v) => set("eta", v)} placeholder="e.g. 15 Jul 2026" />
        </div>
        <Field label="Note (optional)" value={form.note} onChange={(v) => set("note", v)} />
        <Button onClick={create} disabled={busy} data-testid="create-order-btn" className="gap-2"><Plus size={15} /> {busy ? "Creating…" : "Create order"}</Button>
      </Card>

      {orders.length === 0 ? (
        <div className="h-24 flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border rounded-md">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.order_code}`} className="bg-card border border-border rounded-md p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <button onClick={() => copy(o.order_code)} className="inline-flex items-center gap-1.5 font-mono font-semibold text-sm hover:text-primary" data-testid={`copy-${o.order_code}`}>
                    {o.order_code} <Copy size={13} />
                  </button>
                  <p className="text-sm mt-0.5">{o.patient_name}{o.device ? ` · ${o.device}` : ""}{o.phone ? ` · ${o.phone}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select className="rounded-md border border-border bg-background p-2 text-sm outline-none focus:border-primary" value={o.status}
                    onChange={(e) => updateStatus(o, e.target.value)} data-testid={`order-status-${o.order_code}`}>
                    {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                    {!stages.includes(o.status) && <option value={o.status}>{o.status}</option>}
                  </select>
                  <Button size="icon" variant="ghost" onClick={() => del(o)} className="h-9 w-9 hover:text-destructive"><Trash size={15} /></Button>
                </div>
              </div>
              {o.eta && <p className="text-xs text-muted-foreground mt-2">ETA: {o.eta}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WebsiteAdmin() {
  const [c, setC] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/site/content").then(({ data }) => setC(data));
  const loadCb = () => api.get("/site/callbacks").then(({ data }) => setCallbacks(data)).catch(() => {});
  useEffect(() => { load(); loadCb(); }, []);

  const upd = (path, value) => setC((prev) => { const n = clone(prev); let o = n; for (let i = 0; i < path.length - 1; i++) o = o[path[i]]; o[path[path.length - 1]] = value; return n; });
  const updArr = (key, value) => setC((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/site/content", { content: c }); toast.success("Website updated — changes are live"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const markHandled = async (cb) => { await api.patch(`/site/callbacks/${cb.id}`); loadCb(); };

  if (!c) return <div className="p-8"><div className="h-6 w-40 bg-muted rounded animate-pulse" /></div>;
  const pending = callbacks.filter((x) => !x.handled).length;

  return (
    <div data-testid="website-admin-page">
      <PageHeader title="Website" subtitle="Edit your public website content — no code required">
        <Button onClick={save} disabled={busy} data-testid="save-website-button" className="bg-primary hover:bg-primary/90 gap-2"><FloppyDisk size={17} weight="bold" /> {busy ? "Saving…" : "Save & publish"}</Button>
      </PageHeader>

      <div className="p-6 md:p-8">
        <Tabs defaultValue="theme">
          <TabsList className="flex-wrap h-auto">
            {["theme", "menu", "hero", "highlights", "welcome", "services", "testimonials", "bionic", "quote", "track", "orders", "photos", "videos", "downloads", "about", "why", "process", "faq", "cta", "tourism", "banners", "contact", "footer", "callbacks", "quotes"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`web-tab-${t}`} className="capitalize">{{ theme: "Theme & Colours", menu: "Menu / Navigation", tourism: "Medical Tourism", why: "Why choose", cta: "CTA", faq: "FAQ", quote: "Get A Quote", track: "Track Order", orders: "Orders", quotes: "Quote Requests", bionic: "AI Bionic" }[t] || t}{t === "callbacks" && pending > 0 ? ` (${pending})` : ""}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="theme" className="mt-5"><Card>
            <p className="text-sm font-semibold flex items-center gap-2"><Palette size={16} /> Brand colours</p>
            <p className="text-xs text-muted-foreground">These colours apply across the entire site and app — buttons, links, highlights, the top bar and more.</p>
            <ColorField label="Primary colour" value={c.theme?.primary} onChange={(v) => upd(["theme", "primary"], v)} />
            <ColorField label="Primary colour — hover / dark shade" value={c.theme?.primary_dark} onChange={(v) => upd(["theme", "primary_dark"], v)} />
          </Card></TabsContent>

          <TabsContent value="menu" className="mt-5 space-y-4">
            <Card>
              <p className="text-sm font-semibold">Highlighted menu</p>
              <p className="text-xs text-muted-foreground">The featured button in the top navigation.</p>
              <Field label="Highlighted menu label" value={c.nav?.bionic_label} onChange={(v) => upd(["nav", "bionic_label"], v)} />
            </Card>
            <Card>
              <p className="text-sm font-semibold">Main menu items</p>
              <p className="text-xs text-muted-foreground">Rename any menu item. The page each item links to stays the same.</p>
              {(c.nav?.main || []).map((m, i) => (
                <div key={i} className="space-y-1">
                  <Field label={`Menu item ${i + 1}`} value={m.label} onChange={(v) => upd(["nav", "main", i, "label"], v)} />
                  <p className="text-[11px] text-muted-foreground pl-0.5">Links to <code className="text-primary">{m.to}</code></p>
                </div>
              ))}
            </Card>
            <Card>
              <p className="text-sm font-semibold">Top utility bar links</p>
              {(c.nav?.utility || []).map((m, i) => (
                <div key={i} className="space-y-1">
                  <Field label={`Utility link ${i + 1}`} value={m.label} onChange={(v) => upd(["nav", "utility", i, "label"], v)} />
                  <p className="text-[11px] text-muted-foreground pl-0.5">Links to <code className="text-primary">{m.to}</code></p>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="hero" className="mt-5"><Card>
            <Field label="Eyebrow" value={c.hero.eyebrow} onChange={(v) => upd(["hero", "eyebrow"], v)} />
            <Field label="Title (tagline)" value={c.hero.title} onChange={(v) => upd(["hero", "title"], v)} />
            <Field label="Subtitle" textarea value={c.hero.subtitle} onChange={(v) => upd(["hero", "subtitle"], v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Primary button" value={c.hero.cta_primary} onChange={(v) => upd(["hero", "cta_primary"], v)} />
              <Field label="Secondary button" value={c.hero.cta_secondary} onChange={(v) => upd(["hero", "cta_secondary"], v)} />
            </div>
            <ImageField label="Hero image" value={c.hero.image} onChange={(v) => upd(["hero", "image"], v)} />
          </Card></TabsContent>

          <TabsContent value="highlights" className="mt-5 space-y-4">
            {c.highlights.map((h, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Highlight {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => updArr("highlights", c.highlights.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Title" value={h.title} onChange={(v) => upd(["highlights", i, "title"], v)} />
                  <Field label="Subtitle" value={h.subtitle} onChange={(v) => upd(["highlights", i, "subtitle"], v)} />
                </div>
                <Field label="Description" textarea value={h.desc} onChange={(v) => upd(["highlights", i, "desc"], v)} />
                <ImageField label="Image" value={h.image} onChange={(v) => upd(["highlights", i, "image"], v)} />
              </Card>
            ))}
            <Button variant="outline" onClick={() => updArr("highlights", [...c.highlights, { title: "", subtitle: "", desc: "", image: "" }])} className="gap-1.5"><Plus size={14} /> Add highlight</Button>
          </TabsContent>

          <TabsContent value="welcome" className="mt-5"><Card>
            <Field label="Heading" value={c.welcome.heading} onChange={(v) => upd(["welcome", "heading"], v)} />
            <Field label="Body" textarea rows={5} value={c.welcome.body} onChange={(v) => upd(["welcome", "body"], v)} />
            <ImageField label="Image" value={c.welcome.image} onChange={(v) => upd(["welcome", "image"], v)} />
            <div>
              <Label className="text-xs">Stats</Label>
              <div className="mt-2 space-y-2">
                {(c.welcome.stats || []).map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={s.value} onChange={(e) => upd(["welcome", "stats", i, "value"], e.target.value)} placeholder="5,000+" className="w-32" />
                    <Input value={s.label} onChange={(e) => upd(["welcome", "stats", i, "label"], e.target.value)} placeholder="lives helped" />
                    <Button size="icon" variant="ghost" onClick={() => upd(["welcome", "stats"], c.welcome.stats.filter((_, idx) => idx !== i))} className="h-9 w-9 hover:text-destructive"><Trash size={14} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => upd(["welcome", "stats"], [...(c.welcome.stats || []), { value: "", label: "" }])} className="gap-1.5"><Plus size={14} /> Add stat</Button>
              </div>
            </div>
          </Card></TabsContent>

          <TabsContent value="services" className="mt-5 space-y-4">
            {c.services.map((s, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Service {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => updArr("services", c.services.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <Field label="Title" value={s.title} onChange={(v) => upd(["services", i, "title"], v)} />
                <Field label="Description" textarea value={s.desc} onChange={(v) => upd(["services", i, "desc"], v)} />
              </Card>
            ))}
            <Button variant="outline" onClick={() => updArr("services", [...c.services, { title: "", desc: "" }])} className="gap-1.5"><Plus size={14} /> Add service</Button>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-5 space-y-4">
            {c.testimonials.map((t, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Testimonial {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => updArr("testimonials", c.testimonials.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <Field label="Quote" textarea value={t.quote} onChange={(v) => upd(["testimonials", i, "quote"], v)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" value={t.name} onChange={(v) => upd(["testimonials", i, "name"], v)} />
                  <Field label="Location" value={t.location} onChange={(v) => upd(["testimonials", i, "location"], v)} />
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={() => updArr("testimonials", [...c.testimonials, { quote: "", name: "", location: "" }])} className="gap-1.5"><Plus size={14} /> Add testimonial</Button>
          </TabsContent>

          <TabsContent value="quote" className="mt-5"><Card>
            <Field label="Heading" value={c.quote?.heading} onChange={(v) => upd(["quote", "heading"], v)} />
            <Field label="Subtitle" textarea value={c.quote?.subtitle} onChange={(v) => upd(["quote", "subtitle"], v)} />
            <ImageField label="Side image" value={c.quote?.image} onChange={(v) => upd(["quote", "image"], v)} />
            <StringList label="Enquiry / service options (dropdown)" items={c.quote?.services || []} onChange={(v) => upd(["quote", "services"], v)} />
            <Field label="Success message" textarea value={c.quote?.success_message} onChange={(v) => upd(["quote", "success_message"], v)} />
          </Card></TabsContent>

          <TabsContent value="track" className="mt-5"><Card>
            <Field label="Heading" value={c.track?.heading} onChange={(v) => upd(["track", "heading"], v)} />
            <Field label="Subtitle" textarea value={c.track?.subtitle} onChange={(v) => upd(["track", "subtitle"], v)} />
            <ImageField label="Banner image" value={c.track?.image} onChange={(v) => upd(["track", "image"], v)} />
            <StringList label="Order status stages (timeline)" items={c.track?.stages || []} onChange={(v) => upd(["track", "stages"], v)} />
            <p className="text-xs text-muted-foreground">Create and update customer orders in the <b>Orders</b> tab. Customers track them at <b>/track-order</b>.</p>
          </Card></TabsContent>

          <TabsContent value="orders" className="mt-5">
            <OrdersManager stages={c.track?.stages || []} />
          </TabsContent>

          <TabsContent value="photos" className="mt-5 space-y-4">
            <Card>
              <Field label="Heading" value={c.photos?.heading} onChange={(v) => upd(["photos", "heading"], v)} />
              <Field label="Subtitle" textarea value={c.photos?.subtitle} onChange={(v) => upd(["photos", "subtitle"], v)} />
            </Card>
            {(c.photos?.items || []).map((p, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Photo {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => upd(["photos", "items"], c.photos.items.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Title / caption" value={p.title} onChange={(v) => upd(["photos", "items", i, "title"], v)} />
                  <Field label="Album" value={p.album} onChange={(v) => upd(["photos", "items", i, "album"], v)} placeholder="e.g. Clinic, Workshop" />
                </div>
                <ImageField label="Image" value={p.image} onChange={(v) => upd(["photos", "items", i, "image"], v)} />
              </Card>
            ))}
            <Button variant="outline" onClick={() => upd(["photos", "items"], [...(c.photos?.items || []), { id: `p${Date.now()}`, title: "", album: "", image: "" }])} className="gap-1.5"><Plus size={14} /> Add photo</Button>
          </TabsContent>

          <TabsContent value="videos" className="mt-5 space-y-4">
            <Card>
              <Field label="Heading" value={c.videos?.heading} onChange={(v) => upd(["videos", "heading"], v)} />
              <Field label="Subtitle" textarea value={c.videos?.subtitle} onChange={(v) => upd(["videos", "subtitle"], v)} />
            </Card>
            {(c.videos?.items || []).map((v2, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Video {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => upd(["videos", "items"], c.videos.items.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <Field label="Title" value={v2.title} onChange={(v) => upd(["videos", "items", i, "title"], v)} />
                <Field label="YouTube / Vimeo link" value={v2.url} onChange={(v) => upd(["videos", "items", i, "url"], v)} placeholder="https://youtube.com/watch?v=…" />
                <ImageField label="Custom thumbnail (optional)" value={v2.thumbnail} onChange={(v) => upd(["videos", "items", i, "thumbnail"], v)} />
              </Card>
            ))}
            <Button variant="outline" onClick={() => upd(["videos", "items"], [...(c.videos?.items || []), { id: `v${Date.now()}`, title: "", url: "", thumbnail: "" }])} className="gap-1.5"><Plus size={14} /> Add video</Button>
          </TabsContent>

          <TabsContent value="downloads" className="mt-5 space-y-4">
            <Card>
              <Field label="Heading" value={c.downloads?.heading} onChange={(v) => upd(["downloads", "heading"], v)} />
              <Field label="Subtitle" textarea value={c.downloads?.subtitle} onChange={(v) => upd(["downloads", "subtitle"], v)} />
            </Card>
            {(c.downloads?.items || []).map((d, i) => (
              <Card key={i}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Download {i + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => upd(["downloads", "items"], c.downloads.items.filter((_, idx) => idx !== i))} className="h-8 w-8 hover:text-destructive"><Trash size={14} /></Button></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Title" value={d.title} onChange={(v) => upd(["downloads", "items", i, "title"], v)} />
                  <Field label="Category" value={d.category} onChange={(v) => upd(["downloads", "items", i, "category"], v)} placeholder="e.g. Brochure, Form" />
                </div>
                <Field label="Description" textarea value={d.desc} onChange={(v) => upd(["downloads", "items", i, "desc"], v)} />
                <FileField label="File" value={d.file} filename={d.filename} onChange={(path, filename) => { upd(["downloads", "items", i, "file"], path); if (filename) upd(["downloads", "items", i, "filename"], filename); }} />
              </Card>
            ))}
            <Button variant="outline" onClick={() => upd(["downloads", "items"], [...(c.downloads?.items || []), { id: `d${Date.now()}`, title: "", desc: "", category: "", file: "", filename: "" }])} className="gap-1.5"><Plus size={14} /> Add download</Button>
          </TabsContent>

          <TabsContent value="quotes" className="mt-5">
            <QuotesInbox />
          </TabsContent>

          <TabsContent value="bionic" className="mt-5 space-y-4">
            <Card>
              <Field label="Eyebrow / tagline" value={c.bionic?.eyebrow} onChange={(v) => upd(["bionic", "eyebrow"], v)} />
              <Field label="Heading (menu page title)" value={c.bionic?.heading} onChange={(v) => upd(["bionic", "heading"], v)} />
              <Field label="Subtitle" textarea value={c.bionic?.subtitle} onChange={(v) => upd(["bionic", "subtitle"], v)} />
              <ImageField label="Hero background image" value={c.bionic?.hero_image} onChange={(v) => upd(["bionic", "hero_image"], v)} />
              <Field label="Intro paragraph" textarea value={c.bionic?.intro} onChange={(v) => upd(["bionic", "intro"], v)} />
            </Card>

            <Card>
              <p className="text-sm font-semibold">Feature cards</p>
              {(c.bionic?.features || []).map((f, i) => (
                <div key={i} className="rounded-md border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between"><p className="text-xs font-medium text-muted-foreground">Feature {i + 1}</p>
                    <Button size="icon" variant="ghost" onClick={() => upd(["bionic", "features"], c.bionic.features.filter((_, idx) => idx !== i))} className="h-7 w-7 hover:text-destructive"><Trash size={13} /></Button></div>
                  <Field label="Title" value={f.title} onChange={(v) => upd(["bionic", "features", i, "title"], v)} />
                  <Field label="Description" textarea value={f.desc} onChange={(v) => upd(["bionic", "features", i, "desc"], v)} />
                </div>
              ))}
              <Button variant="outline" onClick={() => upd(["bionic", "features"], [...(c.bionic?.features || []), { title: "", desc: "" }])} className="gap-1.5"><Plus size={14} /> Add feature</Button>
            </Card>

            <Card>
              <p className="text-sm font-semibold">Stats bar</p>
              {(c.bionic?.stats || []).map((s, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1"><Field label="Value" value={s.value} onChange={(v) => upd(["bionic", "stats", i, "value"], v)} /></div>
                  <div className="flex-1"><Field label="Label" value={s.label} onChange={(v) => upd(["bionic", "stats", i, "label"], v)} /></div>
                  <Button size="icon" variant="ghost" onClick={() => upd(["bionic", "stats"], c.bionic.stats.filter((_, idx) => idx !== i))} className="h-9 w-9 hover:text-destructive"><Trash size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => upd(["bionic", "stats"], [...(c.bionic?.stats || []), { value: "", label: "" }])} className="gap-1.5"><Plus size={14} /> Add stat</Button>
            </Card>

            <Card>
              <p className="text-sm font-semibold">Gallery images</p>
              {(c.bionic?.gallery || []).map((g, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1"><ImageField label={`Image ${i + 1}`} value={g} onChange={(v) => upd(["bionic", "gallery", i], v)} /></div>
                  <Button size="icon" variant="ghost" onClick={() => upd(["bionic", "gallery"], c.bionic.gallery.filter((_, idx) => idx !== i))} className="h-9 w-9 hover:text-destructive"><Trash size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => upd(["bionic", "gallery"], [...(c.bionic?.gallery || []), ""])} className="gap-1.5"><Plus size={14} /> Add image</Button>
            </Card>

            <Card>
              <p className="text-sm font-semibold">Call to action</p>
              <Field label="CTA heading" value={c.bionic?.cta_title} onChange={(v) => upd(["bionic", "cta_title"], v)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Button text" value={c.bionic?.cta_text} onChange={(v) => upd(["bionic", "cta_text"], v)} />
                <Field label="Button link" value={c.bionic?.cta_link} onChange={(v) => upd(["bionic", "cta_link"], v)} placeholder="/get-a-quote" />
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="about" className="mt-5"><Card>
            <Field label="Heading" value={c.about.heading} onChange={(v) => upd(["about", "heading"], v)} />
            <Field label="Body" textarea rows={4} value={c.about.body} onChange={(v) => upd(["about", "body"], v)} />
            <Field label="Mission" textarea value={c.about.mission} onChange={(v) => upd(["about", "mission"], v)} />
            <Field label="Vision" textarea value={c.about.vision} onChange={(v) => upd(["about", "vision"], v)} />
            <ImageField label="Image" value={c.about.image} onChange={(v) => upd(["about", "image"], v)} />
          </Card></TabsContent>

          <TabsContent value="why" className="mt-5"><Card>
            <Field label="Heading" value={c.why_choose.heading} onChange={(v) => upd(["why_choose", "heading"], v)} />
            <Field label="Subtitle" value={c.why_choose.subtitle} onChange={(v) => upd(["why_choose", "subtitle"], v)} />
            <div className="space-y-3">
              {c.why_choose.points.map((p, i) => (
                <div key={i} className="border border-border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between"><p className="text-xs font-semibold">Point {i + 1}</p>
                    <Button size="icon" variant="ghost" onClick={() => upd(["why_choose", "points"], c.why_choose.points.filter((_, idx) => idx !== i))} className="h-7 w-7 hover:text-destructive"><Trash size={13} /></Button></div>
                  <Field label="Title" value={p.title} onChange={(v) => upd(["why_choose", "points", i, "title"], v)} />
                  <Field label="Description" textarea value={p.desc} onChange={(v) => upd(["why_choose", "points", i, "desc"], v)} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => upd(["why_choose", "points"], [...c.why_choose.points, { title: "", desc: "" }])} className="gap-1.5"><Plus size={14} /> Add point</Button>
            </div>
          </Card></TabsContent>

          <TabsContent value="process" className="mt-5"><Card>
            <Field label="Heading" value={c.process.heading} onChange={(v) => upd(["process", "heading"], v)} />
            <Field label="Subtitle" value={c.process.subtitle} onChange={(v) => upd(["process", "subtitle"], v)} />
            <div className="space-y-3">
              {c.process.steps.map((s, i) => (
                <div key={i} className="border border-border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between"><p className="text-xs font-semibold">Step {i + 1}</p>
                    <Button size="icon" variant="ghost" onClick={() => upd(["process", "steps"], c.process.steps.filter((_, idx) => idx !== i))} className="h-7 w-7 hover:text-destructive"><Trash size={13} /></Button></div>
                  <Field label="Title" value={s.title} onChange={(v) => upd(["process", "steps", i, "title"], v)} />
                  <Field label="Description" textarea value={s.desc} onChange={(v) => upd(["process", "steps", i, "desc"], v)} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => upd(["process", "steps"], [...c.process.steps, { title: "", desc: "" }])} className="gap-1.5"><Plus size={14} /> Add step</Button>
            </div>
          </Card></TabsContent>

          <TabsContent value="faq" className="mt-5"><Card>
            <div className="space-y-3">
              {c.faq.map((f, i) => (
                <div key={i} className="border border-border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between"><p className="text-xs font-semibold">Q{i + 1}</p>
                    <Button size="icon" variant="ghost" onClick={() => updArr("faq", c.faq.filter((_, idx) => idx !== i))} className="h-7 w-7 hover:text-destructive"><Trash size={13} /></Button></div>
                  <Field label="Question" value={f.q} onChange={(v) => upd(["faq", i, "q"], v)} />
                  <Field label="Answer" textarea value={f.a} onChange={(v) => upd(["faq", i, "a"], v)} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => updArr("faq", [...c.faq, { q: "", a: "" }])} className="gap-1.5"><Plus size={14} /> Add question</Button>
            </div>
          </Card></TabsContent>

          <TabsContent value="cta" className="mt-5"><Card>
            <Field label="Heading" value={c.cta.heading} onChange={(v) => upd(["cta", "heading"], v)} />
            <Field label="Subtitle" textarea value={c.cta.subtitle} onChange={(v) => upd(["cta", "subtitle"], v)} />
            <Field label="Button text" value={c.cta.button} onChange={(v) => upd(["cta", "button"], v)} />
          </Card></TabsContent>

          <TabsContent value="banners" className="mt-5"><Card>
            <p className="text-xs text-muted-foreground">Header banner images shown at the top of each inner page.</p>
            {["about", "services", "medical_tourism", "testimonials", "contact", "quote", "track", "photos", "videos", "downloads"].map((k) => (
              <ImageField key={k} label={k.replace("_", " ")} value={c.banners?.[k]} onChange={(v) => upd(["banners", k], v)} />
            ))}
          </Card></TabsContent>

          <TabsContent value="tourism" className="mt-5"><Card>
            <Field label="Heading" value={c.medical_tourism.heading} onChange={(v) => upd(["medical_tourism", "heading"], v)} />
            <Field label="Body" textarea rows={4} value={c.medical_tourism.body} onChange={(v) => upd(["medical_tourism", "body"], v)} />
            <ImageField label="Image" value={c.medical_tourism.image} onChange={(v) => upd(["medical_tourism", "image"], v)} />
            <StringList label="Points" items={c.medical_tourism.points} onChange={(v) => upd(["medical_tourism", "points"], v)} />
          </Card></TabsContent>

          <TabsContent value="contact" className="mt-5"><Card>
            <Field label="Company" value={c.contact.company} onChange={(v) => upd(["contact", "company"], v)} />
            <Field label="Address" textarea value={c.contact.address} onChange={(v) => upd(["contact", "address"], v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone" value={c.contact.phone} onChange={(v) => upd(["contact", "phone"], v)} />
              <Field label="Email" value={c.contact.email} onChange={(v) => upd(["contact", "email"], v)} />
            </div>
            <Field label="Google Map embed URL" value={c.contact.map_embed} onChange={(v) => upd(["contact", "map_embed"], v)} />
          </Card></TabsContent>

          <TabsContent value="footer" className="mt-5"><Card>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone" value={c.footer.phone} onChange={(v) => upd(["footer", "phone"], v)} />
              <Field label="Email" value={c.footer.email} onChange={(v) => upd(["footer", "email"], v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {["facebook", "twitter", "youtube", "linkedin"].map((s) => (
                <Field key={s} label={`${s} URL`} value={c.footer.socials?.[s]} onChange={(v) => upd(["footer", "socials", s], v)} />
              ))}
            </div>
            <StringList label="Quick links" items={c.footer.quick_links} onChange={(v) => upd(["footer", "quick_links"], v)} />
            <StringList label="Products" items={c.footer.products} onChange={(v) => upd(["footer", "products"], v)} />
            <StringList label="Locations" items={c.footer.locations} onChange={(v) => upd(["footer", "locations"], v)} />
          </Card></TabsContent>

          <TabsContent value="callbacks" className="mt-5">
            {callbacks.length === 0 ? <div className="h-32 flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border rounded-md">No callback requests yet.</div> : (
              <div className="bg-card border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left"><tr>{["Name", "Contact", "Location", "When", ""].map((h) => <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-border">
                    {callbacks.map((cb) => (
                      <tr key={cb.id} data-testid={`callback-row-${cb.id}`} className={cb.handled ? "opacity-50" : ""}>
                        <td className="px-4 py-3 font-medium">{cb.name}</td>
                        <td className="px-4 py-3"><a href={`tel:${cb.contact}`} className="text-primary hover:underline inline-flex items-center gap-1"><Phone size={13} />{cb.contact}</a></td>
                        <td className="px-4 py-3 text-muted-foreground">{cb.location || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(cb.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{cb.handled ? <span className="text-xs text-success">✓ Handled</span> : <Button size="sm" variant="outline" onClick={() => markHandled(cb)} data-testid={`callback-handle-${cb.id}`}>Mark handled</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
